import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  Radio,
  FormControlLabel,
  Alert,
  CircularProgress,
  Collapse,
  Card,
  CardContent,
  Divider,
  Stack,
  styled,
  useTheme,
  Autocomplete
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ForwardToInbox as ForwardIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { alpha } from '@mui/material/styles';
import { RTLTextField, RTLFormControl, RTLAutocomplete } from '../../styles/rtlComponents';
import useRTL from '../../hooks/useRTL';

// Using centralized RTL components from styles/rtlComponents.js

const RTLRadioGroup = styled(RadioGroup)(({ theme, isRTL }) => ({
  ...(isRTL && {
    flexDirection: 'row-reverse',
    '& .MuiFormControlLabel-root': {
      marginRight: 0,
      marginLeft: 16,
    },
  }),
}));

// Document Receipt Actions
export const DocumentReceiptActions = ({ onReceive, onRejectReceipt, loading, error }) => {
  const { t, i18n } = useTranslation(['tasks']);
  const theme = useTheme();
  const { isRTL, getRTLProps } = useRTL();
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  const handleRejectReceipt = () => {
    if (!rejectionReason.trim()) return;
    onRejectReceipt(rejectionReason);
    setRejectionReason('');
    setShowRejectForm(false);
  };

  return (
    <Card elevation={1} sx={{ mb: 2.5, borderRadius: 2 }}>
      <CardContent sx={{ p: 2.5 }}>
        <Stack spacing={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <ReceiptIcon color="primary" sx={{ fontSize: 20 }} />
            <Typography variant="h6" fontWeight={600}>
              {t('tasks:document_receipt')}
            </Typography>
          </Box>
          
          <Typography variant="body2" color="text.secondary">
            {t('tasks:document_receipt_confirmation')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>
          )}

          <Stack direction="row" justifyContent="center">
            <LoadingButton
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              loading={loading.receive}
              onClick={onReceive}
              size="medium"
              sx={{ 
                minWidth: 160,
                ml: 1,
                '& .MuiButton-startIcon': {
                  marginRight: isRTL ? 0 : 10,
                  marginLeft: isRTL ? 2 : 0
                },
                '& .MuiButton-startIcon > *:nth-of-type(1)': {
                  fontSize: 20
                }
              }}
            >
              {t('tasks:confirm_receipt')}
            </LoadingButton>
            
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={() => setShowRejectForm(!showRejectForm)}
              disabled={loading.receive}
              size="medium"
              sx={{ 
                minWidth: 160,
                px: 3,
                py: 1.5,
                '& .MuiButton-startIcon': {
                  marginRight: isRTL ? 0 : 10,
                  marginLeft: isRTL ? 2 : 0
                },
                '& .MuiButton-startIcon > *:nth-of-type(1)': {
                  fontSize: 20
                }
              }}
            >
              {t('tasks:reject_receipt')}
            </Button>
          </Stack>

          <Collapse in={showRejectForm}>
            <Box mt={2}>
              <RTLTextField
                fullWidth
                multiline
                rows={3}
                label={t('tasks:rejection_reason')}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                error={!rejectionReason.trim() && showRejectForm}
                helperText={!rejectionReason.trim() && showRejectForm ? t('tasks:reason_required') : ''}
                autoFocus
                {...getRTLProps('textfield')}
                sx={{
                    mb: 2,
                    '& .MuiInputBase-input': {
                      paddingRight: isRTL ? 1 : 8,
                      paddingLeft: isRTL ? 1 : 8,
                      textIndent: 0, // Remove text indentation
                      textAlign: isRTL ? 'right' : 'left'
                    },
                    '& .MuiInputBase-root': {
                      textAlign: isRTL ? 'right' : 'left'
                    }
                  }}
              />
              
              <Stack direction="row" justifyContent="center">
                <LoadingButton
                  variant="contained"
                  color="error"
                  loading={loading.rejectReceipt}
                  onClick={handleRejectReceipt}
                  disabled={!rejectionReason.trim()}
                  size="medium"
                  sx={{ minWidth: 120, ml: 1 }}
                >
                  {t('tasks:confirm_rejection')}
                </LoadingButton>
                
                <Button
                  variant="outlined"
                  onClick={() => {
                    setShowRejectForm(false);
                    setRejectionReason('');
                  }}
                  disabled={loading.rejectReceipt}
                  size="medium"
                  sx={{ minWidth: 120 }}
                >
                  {t('common:cancel')}
                </Button>
              </Stack>
            </Box>
          </Collapse>
        </Stack>
      </CardContent>
    </Card>
  );
};

// Task Completion Actions
export const TaskCompletionActions = ({ onComplete, onReject, onForward, loading, error, users = [], departments = [], isInformationalOnly = false }) => {
  const { t, i18n } = useTranslation(['tasks']);
  const theme = useTheme();
  const { isRTL, getRTLProps } = useRTL();
  const [activeAction, setActiveAction] = useState(null);
  const [completionNotes, setCompletionNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  const handleComplete = () => {
    if (!completionNotes.trim()) return;
    onComplete(completionNotes);
    setCompletionNotes('');
    setActiveAction(null);
  };

  const handleReject = () => {
    if (!rejectionReason.trim()) return;
    onReject(rejectionReason);
    setRejectionReason('');
    setActiveAction(null);
  };

  return (
    <Card elevation={1} sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: 2.5, border: '1px solid #e0e0e0' }}>
        <Stack spacing={0}>
          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>
          )}

          <Stack direction="row" justifyContent="center">
            <LoadingButton
              variant="outlined"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={() => setActiveAction('complete')}
              disabled={loading.complete || loading.reject || loading.forward}
              size="medium"
              sx={{ 
                minWidth: 160,
                px: 3,
                py: 1.5,
                ml: 1,
                // mr: 3,
                '& .MuiButton-startIcon': {
                  marginRight: isRTL ? 0 : 10,
                  marginLeft: isRTL ? 2 : 0
                },
                '& .MuiButton-startIcon > *:nth-of-type(1)': {
                  fontSize: 20
                }
              }}
            >
              {t('tasks:complete_task')}
            </LoadingButton>
            
            {!isInformationalOnly && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ForwardIcon />}
                onClick={() => {
                  setActiveAction('forward');
                  // Clear any existing error when starting a new action
                  if (error) {
                    // The error will be cleared by the parent component
                  }
                }}
                disabled={loading.complete || loading.reject || loading.forward}
                size="medium"
                sx={{ 
                  minWidth: 160,
                  px: 3,
                  py: 1.5,
                  ml: 1,
                  // mr: 3,
                  '& .MuiButton-startIcon': {
                    marginRight: isRTL ? 0 : 10,
                    marginLeft: isRTL ? 2 : 2,
                  },
                  '& .MuiButton-startIcon > *:nth-of-type(1)': {
                    fontSize: 20
                  }
                }}
              >
                {t('tasks:forward_task')}
              </Button>
            )}
            
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={() => setActiveAction('reject')}
              disabled={loading.complete || loading.reject || loading.forward}
              size="medium"
              sx={{ 
                minWidth: 160,
                px: 3,
                py: 1.5,
                '& .MuiButton-startIcon': {
                  marginRight: isRTL ? 0 : 10,
                  marginLeft: isRTL ? 2 : 0
                },
                '& .MuiButton-startIcon > *:nth-of-type(1)': {
                  fontSize: 20
                }
              }}
            >
              {t('tasks:reject_task')}
            </Button>
          </Stack>

          {/* Complete Task Form */}
          <Collapse in={activeAction === 'complete'}>
            <Box mt={2} p={2} sx={{ 
              border: 1, 
              borderColor: 'success.main', 
              borderRadius: 2, 
              bgcolor: alpha('#4caf50', 0.02) 
            }}>
              <Typography variant="subtitle2" fontWeight={600} color="success.main" sx={{ mb: 1.5 }}>
                {t('tasks:complete_task')}
              </Typography>
              
              <RTLTextField
                fullWidth
                multiline
                rows={3}
                label={t('tasks:completion_notes')}
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
                error={!completionNotes.trim() && activeAction === 'complete'}
                helperText={!completionNotes.trim() && activeAction === 'complete' ? t('tasks:notes_required') : ''}
                autoFocus
                {...getRTLProps('textfield')}
                sx={{
                    mb: 2,
                    '& .MuiInputBase-input': {
                      paddingRight: isRTL ? 1 : 8,
                      paddingLeft: isRTL ? 1 : 8,
                      textIndent: 0, // Remove text indentation
                      textAlign: isRTL ? 'right' : 'left'
                    },
                    '& .MuiInputBase-root': {
                      textAlign: isRTL ? 'right' : 'left'
                    }
                  }}
              />
              
              <Stack direction="row" justifyContent="center">
                <LoadingButton
                  variant="contained"
                  color="success"
                  loading={loading.complete}
                  onClick={handleComplete}
                  disabled={!completionNotes.trim()}
                  size="medium"
                  sx={{ minWidth: 120, ml: 1 }}
                >
                  {t('tasks:confirm_completion')}
                </LoadingButton>
                
                <Button
                  variant="outlined"
                  onClick={() => {
                    setActiveAction(null);
                    setCompletionNotes('');
                  }}
                  disabled={loading.complete}
                  size="medium"
                  sx={{ minWidth: 120 }}
                >
                  {t('common:cancel')}
                </Button>
              </Stack>
            </Box>
          </Collapse>

          {/* Reject Task Form */}
          <Collapse in={activeAction === 'reject'}>
            <Box mt={2} p={2} sx={{ 
              border: 1, 
              borderColor: 'error.main', 
              borderRadius: 2, 
              bgcolor: alpha('#f44336', 0.02) 
            }}>
              <Typography variant="subtitle2" fontWeight={600} color="error.main" sx={{ mb: 1.5 }}>
                {t('tasks:reject_task')}
              </Typography>
              
              <RTLTextField
                fullWidth
                multiline
                rows={3}
                label={t('tasks:rejection_reason')}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                error={!rejectionReason.trim() && activeAction === 'reject'}
                helperText={!rejectionReason.trim() && activeAction === 'reject' ? t('tasks:reason_required') : ''}
                autoFocus
                {...getRTLProps('textfield')}
                sx={{
                    mb: 2,
                    '& .MuiInputBase-input': {
                      paddingRight: isRTL ? 1 : 8,
                      paddingLeft: isRTL ? 1 : 8,
                      textIndent: 0, // Remove text indentation
                      textAlign: isRTL ? 'right' : 'left'
                    },
                    '& .MuiInputBase-root': {
                      textAlign: isRTL ? 'right' : 'left'
                    }
                  }}
              />
              
              <Stack direction="row" justifyContent="center">
                <LoadingButton
                  variant="contained"
                  color="error"
                  loading={loading.reject}
                  onClick={handleReject}
                  disabled={!rejectionReason.trim()}
                  size="medium"
                  sx={{ minWidth: 120, ml: 1 }}
                >
                  {t('tasks:confirm_rejection')}
                </LoadingButton>
                
                <Button
                  variant="outlined"
                  onClick={() => {
                    setActiveAction(null);
                    setRejectionReason('');
                  }}
                  disabled={loading.reject}
                  size="medium"
                  sx={{ minWidth: 120, ml: 1 }}
                >
                  {t('common:cancel')}
                </Button>
              </Stack>
            </Box>
          </Collapse>

          {/* Forward Task Form */}
          <Collapse in={activeAction === 'forward'}>
            <ForwardTaskForm
              onForward={onForward}
              loading={loading}
              error={error}
              onCancel={() => setActiveAction(null)}
              users={users}
              departments={departments}
            />
          </Collapse>
        </Stack>
      </CardContent>
    </Card>
  );
};

// Forward Task Form Component
const ForwardTaskForm = ({ onForward, loading, error, onCancel, users = [], departments = [] }) => {
  const { t } = useTranslation(['tasks']);
  const { isRTL, getRTLProps } = useRTL();
  const [selectedUser, setSelectedUser] = useState(null);
  const [forwardNotes, setForwardNotes] = useState('');

  const handleForward = () => {
    if (!selectedUser || !forwardNotes.trim()) return;
    
    onForward({
      recipient: selectedUser.id, // Changed from 'target' to 'recipient'
      reason: forwardNotes,    // Changed from 'notes' to 'reason'
      type: 'user'
    });
    
    setSelectedUser(null);
    setForwardNotes('');
  };

  return (
    <Box mt={2} p={2} sx={{ 
      border: 1, 
      borderColor: 'primary.main', 
      borderRadius: 2, 
      bgcolor: alpha('#1976d2', 0.02) 
    }}>
      <Typography variant="subtitle2" fontWeight={600} color="primary.main" sx={{ mb: 1.5 }}>
        {t('tasks:forward_task')}
      </Typography>
      
      <Stack spacing={2}>
        <RTLAutocomplete
          fullWidth
          {...getRTLProps('autocomplete')}
          options={users}
          getOptionLabel={(option) => option.full_name || option.username || t('common:unnamed_user', 'Unnamed user')}
          value={selectedUser}
          onChange={(event, newValue) => setSelectedUser(newValue)}
          loading={loading.forward}
          disabled={loading.forward}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('tasks:select_user')}
              placeholder={t('tasks:search_user', 'Search for a user...')}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading.forward ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                },
                '& .MuiAutocomplete-clearIndicator': {
                  display: 'none !important',
                },
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} sx={{ p: 2, borderBottom: '1px solid #f0f0f0', '&:hover': { bgcolor: '#f8f9fa' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <Box 
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    borderRadius: '50%', 
                    bgcolor: 'primary.main', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.875rem',
                    flexShrink: 0
                  }}
                >
                  {(option.full_name || option.username || 'U').charAt(0).toUpperCase()}
                </Box>
                <Box sx={{ flexGrow: 1, textAlign: isRTL ? 'right' : 'left', minWidth: 0 }}>
                  <Typography variant="body1" fontWeight="600" sx={{ mb: 0.5 }} noWrap>
                    {option.full_name || option.username || t('common:unnamed_user', 'Unnamed user')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {option.email}
                  </Typography>
                  {option.department?.name && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                      {option.department.name}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
          noOptionsText={t('tasks:no_users_found', 'No users found')}
          loadingText={t('common:loading_users', 'Loading users...')}
          sx={{
            '& .MuiAutocomplete-paper': {
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: '1px solid',
              borderColor: 'divider',
            },
          }}
        />

        <RTLTextField
          fullWidth
          multiline
          rows={3}
          label={t('tasks:forward_notes')}
          value={forwardNotes}
          onChange={(e) => setForwardNotes(e.target.value)}
          {...getRTLProps('textfield')}
          sx={{
            '& .MuiInputBase-input': {
              paddingRight: isRTL ? 1 : 8,
              paddingLeft: isRTL ? 1 : 8,
              textIndent: 0, // Remove text indentation
              textAlign: isRTL ? 'right' : 'left'
            },
            '& .MuiInputBase-root': {
              textAlign: isRTL ? 'right' : 'left'
            }
          }}
        />
        
        <Stack direction="row" justifyContent="center">
          <LoadingButton
            variant="contained"
            color="primary"
            loading={loading.forward}
            onClick={handleForward}
            disabled={!selectedUser || !forwardNotes.trim()}
            size="medium"
            sx={{ minWidth: 120, ml: 1 }}
          >
            {t('tasks:confirm_forward')}
          </LoadingButton>
          
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={loading.forward}
            size="medium"
            sx={{ 
              minWidth: 120,
              ml: 1,
              '& .MuiButton-startIcon': {
                marginRight: isRTL ? 0 : 10,
                marginLeft: isRTL ? 2 : 0
              },
              '& .MuiButton-startIcon > *:nth-of-type(1)': {
                fontSize: 20
              }
            }}
          >
            {t('common:cancel')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}; 